import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TemplateEditor } from "@/components/composer/templates/TemplateEditor";
import { TemplatePreviewDialog } from "@/components/composer/templates/TemplatePreviewDialog";
import type { TemplateSelection } from "@/components/composer/templates/templateTypes";
import type { BriefDocument } from "@/lib/briefDocument";
import type { ExcerptusTemplate } from "@/lib/composerTemplates";
import { useTemplatesPanel } from "./useTemplatesPanel";
import { TemplatesFilters, KindFilterChips } from "./TemplatesFilters";
import { TemplateLists } from "./TemplateLists";

type TemplatesPanelProps = {
  onSelect: (selection: TemplateSelection) => void;
  onSaveCurrent?: () => void;
  canSaveCurrent?: boolean;
  /** Returns the current Draft Canvas BriefDocument, or null when no draft is active. */
  getCurrentDoc?: () => BriefDocument | null;
};

export const TemplatesPanel = ({ onSelect, onSaveCurrent, canSaveCurrent, getCurrentDoc }: TemplatesPanelProps) => {
  const s = useTemplatesPanel(getCurrentDoc);

  return (
    <div className="flex h-full flex-col gap-4 p-3">
      <TemplatesFilters
        search={s.search}
        setSearch={s.setSearch}
        onSaveCurrent={onSaveCurrent}
        canSaveCurrent={canSaveCurrent}
        tabContentTypes={s.tabContentTypes}
        contentTypeFilter={s.contentTypeFilter}
        setContentTypeFilter={s.setContentTypeFilter}
        kindFilter={s.kindFilter}
        setKindFilter={s.setKindFilter}
      />

      <Tabs value={s.tab} onValueChange={(v) => s.setTab(v as "excerptus" | "mine")} className="flex flex-1 flex-col">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="excerptus">Excerptus Templates</TabsTrigger>
          <TabsTrigger value="mine">My Templates</TabsTrigger>
        </TabsList>

        <KindFilterChips kindFilter={s.kindFilter} setKindFilter={s.setKindFilter} />

        <TemplateLists
          loading={s.loading}
          isOwner={s.isOwner}
          filteredExcerptus={s.filteredExcerptus}
          filteredUser={s.filteredUser}
          updateDisabledReason={s.updateDisabledReason}
          getCurrentDoc={getCurrentDoc}
          onSelect={onSelect}
          onPushToCanvas={s.pushTemplateToCanvas}
          onPreview={s.setPreview}
          onClone={s.handleClone}
          onDuplicateMine={s.handleDuplicateMine}
          onEdit={s.setEditing}
          onDelete={s.handleDelete}
          onUpdateFromDraft={s.handleUpdateFromDraft}
          onCreateNew={s.handleCreateNew}
          onDragEnd={s.handleDragEnd}
        />
      </Tabs>

      {s.editing && (
        <TemplateEditor
          mode="user"
          template={s.editing}
          open={Boolean(s.editing)}
          onOpenChange={(o) => !o && s.setEditing(null)}
          onSaved={() => {
            s.setEditing(null);
            void s.refresh();
          }}
        />
      )}

      {s.preview && (
        <TemplatePreviewDialog
          open
          onOpenChange={(o) => !o && s.setPreview(null)}
          name={s.preview.template.name}
          contentType={s.preview.template.content_type}
          description={s.preview.template.description}
          introMessage={s.preview.template.intro_message}
          wizardQuestions={s.preview.template.wizard_questions}
          defaultStructure={s.preview.template.default_structure}
          onUse={() => {
            const t = s.preview!.template;
            onSelect({
              source: s.preview!.kind,
              templateId: t.id,
              name: t.name,
              contentType: t.content_type,
              introMessage: t.intro_message,
              wizardQuestions: t.wizard_questions,
              defaultStructure: t.default_structure,
            });
          }}
          onClone={
            s.preview.kind === "excerptus" && s.isOwner
              ? () => s.handleClone(s.preview!.template as ExcerptusTemplate)
              : undefined
          }
        />
      )}
    </div>
  );
};

export default TemplatesPanel;
